import { trackEmEvent } from "../../features/analytics/em/sdk";
import type {
  EmEventName,
  EmTrackPrimitive,
  EmTrackProps,
} from "../../features/analytics/em/events";
import {
  RechargeSpinDerivedState,
  RechargeSpinInfo,
  RechargeSpinResult,
  RechargeSpinSource,
} from "./types";

type RechargeSpinEntryState = "idle" | "progress" | "ready";
type RechargeSpinCloseSource =
  | "page_close"
  | "rules_back"
  | "close_button"
  | "deposit";

interface RechargeSpinTrackBase {
  source?: RechargeSpinSource;
  info?: RechargeSpinInfo;
  derived?: RechargeSpinDerivedState;
}

interface RechargeSpinEntryTrackOptions extends RechargeSpinTrackBase {
  entryState: RechargeSpinEntryState;
}

interface RechargeSpinResultTrackOptions extends RechargeSpinTrackBase {
  result?: RechargeSpinResult | null;
}

interface RechargeSpinFailureTrackOptions extends RechargeSpinTrackBase {
  errorMessage?: string;
}

interface RechargeSpinClickTrackOptions extends RechargeSpinTrackBase {
  canSpin: boolean;
}

interface RechargeSpinCloseTrackOptions extends RechargeSpinResultTrackOptions {
  closeSource: RechargeSpinCloseSource;
}

const ACTIVITY_KEY = "recharge_spin";

const addDefined = (
  props: EmTrackProps,
  key: string,
  value: EmTrackPrimitive
): void => {
  if (value !== undefined) {
    props[key] = value;
  }
};

const buildRechargeSpinProps = (
  options: RechargeSpinTrackBase = {},
  extraProps: Record<string, EmTrackPrimitive> = {}
): EmTrackProps => {
  const { source, info, derived } = options;
  const props: EmTrackProps = {
    activityKey: ACTIVITY_KEY,
  };

  addDefined(props, "source", source);
  addDefined(props, "surface", source);
  addDefined(props, "availability", derived?.availability);
  addDefined(props, "progressPercent", derived?.progressPercent);
  addDefined(props, "isReady", derived?.isReady);
  addDefined(props, "isLocked", derived?.isLocked);
  addDefined(props, "openStatus", info?.status);
  addDefined(props, "minVipLevel", info?.minVipLevel);
  addDefined(props, "totalExperience", info?.totalExperience);
  addDefined(props, "drawExperience", info?.drawExperience);
  addDefined(props, "availableDrawTimes", info?.availableDrawTimes);
  addDefined(props, "dailyDrawLimit", info?.dailyDrawLimit);
  addDefined(props, "todayDrawTimes", info?.todayDrawTimes);
  addDefined(props, "awardCount", info?.awardList?.length);

  Object.entries(extraProps).forEach(([key, value]) => {
    addDefined(props, key, value);
  });

  return props;
};

const buildResultProps = (
  options: RechargeSpinResultTrackOptions,
  extraProps: Record<string, EmTrackPrimitive> = {}
): EmTrackProps => {
  const { result } = options;

  return buildRechargeSpinProps(options, {
    awardId: result?.awardId,
    awardName: result?.awardName,
    awardAmount: result?.amount,
    awardCurrency: result?.currencyType,
    nextTotalExperience: result?.totalExperience,
    nextAvailableDrawTimes: result?.availableDrawTimes,
    nextTodayDrawTimes: result?.todayDrawTimes,
    ...extraProps,
  });
};

const trackRechargeSpinEvent = (
  eventName: EmEventName,
  props: EmTrackProps
): void => {
  trackEmEvent(eventName, props);
};

export const trackRechargeSpinEntryView = (
  options: RechargeSpinEntryTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_entry_view",
    buildRechargeSpinProps(options, { entryState: options.entryState })
  );
};

export const trackRechargeSpinEntryClick = (
  options: RechargeSpinEntryTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_entry_click",
    buildRechargeSpinProps(options, { entryState: options.entryState })
  );
};

export const trackRechargeSpinActivityView = (
  options: RechargeSpinTrackBase
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_activity_view",
    buildRechargeSpinProps(options)
  );
};

export const trackRechargeSpinActivityClose = (
  options: RechargeSpinCloseTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_activity_close",
    buildResultProps(options, { closeSource: options.closeSource })
  );
};

export const trackRechargeSpinDepositClick = (
  options: RechargeSpinTrackBase
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_deposit_click",
    buildRechargeSpinProps(options)
  );
};

export const trackRechargeSpinRulesView = (
  options: RechargeSpinTrackBase
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_rules_view",
    buildRechargeSpinProps(options)
  );
};

export const trackRechargeSpinRulesClose = (
  options: RechargeSpinTrackBase
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_rules_close",
    buildRechargeSpinProps(options, { closeSource: "rules_back" })
  );
};

export const trackRechargeSpinSpinClick = (
  options: RechargeSpinClickTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_click",
    buildRechargeSpinProps(options, { canSpin: options.canSpin })
  );
};

export const trackRechargeSpinSpinSubmit = (
  options: RechargeSpinTrackBase
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_submit",
    buildRechargeSpinProps(options)
  );
};

export const trackRechargeSpinSpinSuccess = (
  options: RechargeSpinResultTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_success",
    buildResultProps(options)
  );
};

export const trackRechargeSpinSpinFail = (
  options: RechargeSpinFailureTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_fail",
    buildRechargeSpinProps(options, { errorMessage: options.errorMessage })
  );
};

export const trackRechargeSpinResultClose = (
  options: RechargeSpinCloseTrackOptions
): void => {
  trackRechargeSpinEvent(
    "recharge_spin_result_close",
    buildResultProps(options, { closeSource: options.closeSource })
  );
};
